/**
 * UnsafeAreasSettingsPanel
 * Ops UI to view/edit SA unsafe areas with tier explanations and duplicate detection.
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  RISK_TIER_GUIDE,
  TERMINOLOGY,
  buildAreaFromForm,
  findUnsafeAreaDuplicates,
  tierTagColor,
} from "../utils/unsafeAreas.js";

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const PanelRoot = styled.div`
  width: 100%;
`;

const GuideCard = styled.div`
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`;

const PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

/**
 * @param {Object} props
 * @param {Array} props.value - Form-bound areas array (or JSON string from legacy)
 * @param {Function} props.onChange
 */
const UnsafeAreasSettingsPanel = ({ value, onChange }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();

  const areas = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [value]);

  const listDuplicates = useMemo(
    () => findUnsafeAreaDuplicates(areas),
    [areas]
  );

  const emitChange = (next) => {
    if (typeof onChange === "function") {
      onChange(next);
    }
  };

  const handleRemove = (index) => {
    const next = areas.filter((_, i) => i !== index);
    emitChange(next);
    message.success("Area removed — save settings to persist");
  };

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      const entry = buildAreaFromForm(values);

      if (!entry.name) {
        message.error("Place name is required");
        return;
      }

      const { hasDuplicates, conflicts } = findUnsafeAreaDuplicates(
        areas,
        entry
      );
      if (hasDuplicates) {
        message.error(
          `Duplicate detected: "${conflicts[0]?.key}" already exists as ${conflicts[0]?.names?.join(", ")}`
        );
        return;
      }

      emitChange([...areas, entry]);
      form.resetFields();
      setAddOpen(false);
      message.success("Area added — save settings to persist");
    } catch {
      // validation errors shown by form
    }
  };

  const columns = [
    {
      title: "Place",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.regionGroup && record.regionGroup !== name ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Group: {record.regionGroup}
            </Text>
          ) : null}
          {Array.isArray(record.aliases) && record.aliases.length > 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Aliases: {record.aliases.join(", ")}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Province",
      dataIndex: "province",
      key: "province",
      width: 140,
    },
    {
      title: "Tier",
      key: "tier",
      width: 160,
      render: (_, record) => (
        <Tag color={tierTagColor(record.severity)}>
          Tier {record.tier || "—"} · {record.severity || "medium"}
        </Tag>
      ),
    },
    {
      title: "Concern / Protocol",
      key: "protocol",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.primaryLogisticsConcern ? (
            <Text style={{ fontSize: 12 }}>
              <Text type="secondary">Concern: </Text>
              {record.primaryLogisticsConcern}
            </Text>
          ) : null}
          {record.generalSafetyProtocol || record.notes ? (
            <Text style={{ fontSize: 12 }}>
              <Text type="secondary">Protocol: </Text>
              {record.generalSafetyProtocol || record.notes}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          aria-label={`Remove ${areas[index]?.name || "area"}`}
          onClick={() => handleRemove(index)}
        />
      ),
    },
  ];

  return (
    <PanelRoot>
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="How unsafe-area alerts work"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            When a lead or booking pickup/delivery address matches a place name
            (or alias) below, ops see a warning on the lead page and booking
            confirmation. Tier 1–2 matches require acknowledgment before booking
            confirm. Customers never see this list.
          </Paragraph>
        }
      />

      <GuideCard>
        <Title level={5} style={{ marginTop: 0 }}>
          Risk tiers
        </Title>
        {RISK_TIER_GUIDE.map((tier) => (
          <Paragraph key={tier.tier} style={{ marginBottom: 10 }}>
            <Tag color={tierTagColor(tier.severity)}>{tier.label}</Tag>
            <Text>{tier.summary}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Ops action: {tier.opsAction}
            </Text>
          </Paragraph>
        ))}
      </GuideCard>

      <GuideCard>
        <Title level={5} style={{ marginTop: 0 }}>
          Terminology
        </Title>
        {TERMINOLOGY.map((item) => (
          <Paragraph key={item.term} style={{ marginBottom: 8 }}>
            <Text strong>{item.term}: </Text>
            <Text type="secondary">{item.meaning}</Text>
          </Paragraph>
        ))}
      </GuideCard>

      {listDuplicates.hasDuplicates ? (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message="Duplicate place names detected in the list"
          description={listDuplicates.conflicts
            .map(
              (conflict) =>
                `"${conflict.key}" appears under: ${conflict.names.join(", ")}`
            )
            .join(" · ")}
        />
      ) : null}

      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Text type="secondary">{areas.length} place(s) configured</Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddOpen(true)}
        >
          Add area
        </Button>
      </Space>

      <Table
        size="small"
        rowKey={(record, index) =>
          `${record.name || "area"}-${record.province || ""}-${index}`
        }
        columns={columns}
        dataSource={areas}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: true }}
      />

      <Modal
        title="Add unsafe area"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false);
          form.resetFields();
        }}
        onOk={handleAdd}
        okText="Add to list"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Duplicates are blocked"
          description="If the place name or any alias already exists (same spelling after normalization), add will be rejected."
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            tier: 3,
            province: "Gauteng",
          }}
        >
          <Form.Item
            name="name"
            label="Place name"
            rules={[{ required: true, message: "Place name is required" }]}
          >
            <Input placeholder="e.g. Delft" />
          </Form.Item>
          <Form.Item
            name="aliasesText"
            label="Aliases (comma-separated)"
            extra="Optional alternate spellings used for matching"
          >
            <Input placeholder="e.g. Joburg CBD, JHB CBD" />
          </Form.Item>
          <Form.Item name="regionGroup" label="Region group label">
            <Input placeholder="e.g. Delft / Nyanga" />
          </Form.Item>
          <Form.Item
            name="province"
            label="Province"
            rules={[{ required: true, message: "Province is required" }]}
          >
            <Select
              options={PROVINCES.map((province) => ({
                value: province,
                label: province,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="tier"
            label="Risk tier"
            rules={[{ required: true, message: "Tier is required" }]}
          >
            <Select
              options={RISK_TIER_GUIDE.map((tier) => ({
                value: tier.tier,
                label: tier.label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="primaryLogisticsConcern"
            label="Primary logistics concern"
          >
            <Input placeholder="e.g. Vehicle tracking and interception" />
          </Form.Item>
          <Form.Item
            name="generalSafetyProtocol"
            label="General safety protocol"
          >
            <TextArea
              rows={2}
              placeholder="e.g. Maintain constant communication with dispatch."
            />
          </Form.Item>
        </Form>
      </Modal>
    </PanelRoot>
  );
};

export default UnsafeAreasSettingsPanel;
